import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mic, Square, Upload, Loader2, Wand2 } from "lucide-react";

export default function VoiceRecorder({ onTranscribed }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [autoDraft, setAutoDraft] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Mic not available — use 'Upload audio' instead");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await upload(blob, "memo.webm");
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error("Mic permission denied");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    setRecording(false);
  };

  const upload = async (blob, filename) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, filename);
      const { data } = await api.post(`/worklog/voice?auto_draft=${autoDraft}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      toast.success(`Added ${data.bullets.length} bullet(s) to today`);
      onTranscribed?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice failed");
    } finally {
      setUploading(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f, f.name);
    e.target.value = "";
  };

  return (
    <div className="border border-hairline bg-surface p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-mono text-safety">// voice work log</div>
          <div className="font-display font-bold tracking-tightest mt-1">Record or upload</div>
        </div>
        <label className="flex items-center gap-2 label-mono text-bone/60 text-[10px] cursor-pointer">
          <input
            type="checkbox"
            checked={autoDraft}
            onChange={(e) => setAutoDraft(e.target.checked)}
            data-testid="voice-auto-draft-toggle"
            className="accent-safety"
          />
          AUTO-DRAFT POST
        </label>
      </div>
      <p className="text-xs text-bone/55 mb-4">
        30-60s memo → Whisper transcribes → Claude extracts up to 3 sharp bullets and appends to today's log.
      </p>
      <div className="flex flex-wrap gap-2">
        {!recording ? (
          <Button
            onClick={start}
            disabled={uploading}
            data-testid="voice-record-btn"
            className="rounded-none bg-safety hover:bg-safety/90 text-white label-mono h-10"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mic className="w-4 h-4 mr-2" />}
            Record
          </Button>
        ) : (
          <Button
            onClick={stop}
            data-testid="voice-stop-btn"
            className="rounded-none bg-red-500 hover:bg-red-600 text-white label-mono h-10 animate-pulse"
          >
            <Square className="w-4 h-4 mr-2" /> Stop
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || recording}
          data-testid="voice-upload-btn"
          className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono h-10"
        >
          <Upload className="w-4 h-4 mr-2" /> Upload audio
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          onChange={onFile}
          className="hidden"
          data-testid="voice-file-input"
        />
      </div>
    </div>
  );
}
